import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, forkJoin } from 'rxjs';
import { map, take, concatMap } from 'rxjs/operators';
import { IImagesModel } from '../models/image.model';
import { NgForm } from '@angular/forms';
import { apiParameters } from 'src/app/configs/api.params';
import { UserService } from './user.service';
import {
  IVehicles,
  IVehicleModel,
  IVehicleInfo,
} from '../models/vehicle.model';
import { IImages, IFile } from '../models/vehicle-image.model';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class VehicleService {
  constructor(
    private httpClient: HttpClient,
    private userService: UserService,
    private authService: AuthService
  ) {}

  /**
   * Gets a list of vehicles
   * @param page - number of page for pagination
   * @param limit - items to show for page
   * @param vins - optional query filter vins name
   * @param make - optional query filter
   * @param from - optional query filter from date
   * @param to - optional query filter to date
   * @param id - user id
   * @returns List of vehicles
   */
  get(
    page: number,
    limit: number,
    withoutCount: boolean,
    vins: string[],
    make: string,
    from?: string,
    to?: string,
    id?: string,
    licensePlateNumber?: string
  ): Observable<IVehicles> {
    const currentUser = JSON.parse(
      JSON.stringify(this.userService.getLoggedInUser())
    );

    let userId = currentUser.user.id;

    if (id) {
      userId = id;
    }

    let searchParams = new HttpParams();

    if (vins && vins.length > 0) {
      vins.forEach((vin) => {
        searchParams = searchParams.append('vin[]', vin);
      });
    }
    if (make && make !== '') {
      searchParams = searchParams.append('make', make);
    }
    if (from && from !== '') {
      searchParams = searchParams.append('from', from);
    }
    if (to && to !== '') {
      searchParams = searchParams.append('to', to);
    }

    if(licensePlateNumber && licensePlateNumber !== '') {
      searchParams = searchParams.append('licensePlateNumber', licensePlateNumber);
    }

    const httpOptions = {
      headers: new HttpHeaders({
        page: page.toString(),
        limit: limit.toString(),
        // withoutCount: withoutCount.toString(),
      }),
      params: searchParams,
    };

    return this.httpClient
      .get(apiParameters.url + '/users/' + userId + '/vehicles', httpOptions)
      .pipe(
        map((res:any) => (
          {
            count: res.count || null,
            results: res.results || res
          }
        )),
        map((res: IVehicles) => {
          // return res;
          res.results.sort((a, b) => {
            return (
              (new Date(b.createdAt) as any) - (new Date(a.createdAt) as any)
            );
          });
          return res;
        })
      );
  }

  /**
   * Gets vehicles as root
   * @param page - number of page for pagination
   * @param limit - items to show for page
   * @param vins - optional query filter vins name
   * @param make - optional query filter
   * @param from - optional query filter from date
   * @param to - optional query filter to date
   * @returns List of vehicles as root
   */
  getVehiclesAsRoot(
    page: number,
    limit: number,
    withoutCount: boolean,
    vins: string[],
    make: string,
    from?: string,
    to?: string,
    clientName?: string,
    licensePlateNumber?: string,
    customerId?: string,
  ): Observable<IVehicles> {
    let searchParams = new HttpParams();

    if (vins && vins.length > 0) {
      vins.forEach((vin) => {
        searchParams = searchParams.append('vin[]', vin);
      });
    }
    if (make && make !== '') {
      searchParams = searchParams.append('make', make);
    }
    if (from && from !== '') {
      searchParams = searchParams.append('from', from);
    }
    if (to && to !== '') {
      searchParams = searchParams.append('to', to);
    }
    if (clientName && clientName !== '') {
      searchParams = searchParams.append('clientName', clientName);
    }
    if(licensePlateNumber && licensePlateNumber !== '') {
      searchParams = searchParams.append('licensePlateNumber', licensePlateNumber);
    }
    if(customerId && customerId !== '') {
      searchParams = searchParams.append('customerId', customerId);
    }
    
    const httpOptions = {
      headers: new HttpHeaders({
        page: page.toString(),
        limit: limit.toString(),
        // withoutCount: withoutCount.toString(),
      }),
      params: searchParams,
    };

    return this.httpClient
      .get(apiParameters.url + '/vehicles', httpOptions)
      .pipe(
        map((res:any) => (
          {
            count: res.count || null,
            results: res.results || res
          }
        )),
        map((res: IVehicles) => {
          // return res;
          res.results.sort((a, b) => {
            return (
              (new Date(b.createdAt) as any) - (new Date(a.createdAt) as any)
            );
          });
          return res;
        })
      );
  }

   /**
   * Gets vehicles as root
   * @param page - number of page for pagination
   * @param limit - items to show for page
   * @param vins - optional query filter vins name
   * @param make - optional query filter
   * @param from - optional query filter from date
   * @param to - optional query filter to date
   * @returns List of vehicles as root
   */
  getDeletedVehicles(
    page: number,
    limit: number,
    vins: string[],
    filters: any
  ): Observable<IVehicles> {
    let searchParams = new HttpParams();

    if (vins && vins.length > 0) {
      vins.forEach((vin) => {
        searchParams = searchParams.append('vin[]', vin);
      });
    }

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        searchParams = searchParams.append(key, value as string);
      }
    });


    searchParams = searchParams.append('onlyDeleted', 'true')
    
    const httpOptions = {
      headers: new HttpHeaders({
        page: page.toString(),
        limit: limit.toString(),
        // withoutCount: withoutCount.toString(),
      }),
      params: searchParams,
    };

    return this.httpClient
      .get(apiParameters.url + '/vehicles', httpOptions)
      .pipe(
        map((res:any) => (
          {
            count: res.count || null,
            results: res.results || res
          }
        )),
        map((res: IVehicles) => {
          // return res;
          res.results.sort((a, b) => {
            return (
              (new Date(b.createdAt) as any) - (new Date(a.createdAt) as any)
            );
          });
          return res;
        })
      );
  }

  /**
   * Gets all vehicles as customer
   * @param customerId - customer id
   * @param page - number of page for pagination
   * @param limit - items to show for page
   * @param vins - optional query filter vins name
   * @param make - optional query filter
   * @param from - optional query filter from date
   * @param to - optional query filter to date
   * @param userName - optional query filter
   * @param userEmail - optional query filter
   * @returns all vehicles as customer
   */
  getAllVehiclesAsCustomer(
    customerId: string,
    page: number,
    limit: number,
    withoutCount: boolean,
    vins: string[],
    make: string,
    from: string,
    to: string,
    userName: string,
    userEmail?: string,
    childId?: string,
    clientName?: string,
    vehicleStatus?: string,
    allChildIds?:string,
    licensePlateNumber?:string
  ): Observable<IVehicles> {
    let searchParams = new HttpParams();

    if (vins && vins.length > 0) {
      vins.forEach((vin) => {
        searchParams = searchParams.append('vin[]', vin);
      });
    }
    if (make && make !== '') {
      searchParams = searchParams.append('make', make);
    }
    if (from && from !== '') {
      searchParams = searchParams.append('from', from);
    }
    if (to && to !== '') {
      searchParams = searchParams.append('to', to);
    }
    if (userName && userName !== '') {
      searchParams = searchParams.append('userName', userName);
    }
    if (userEmail && userEmail !== '') {
      searchParams = searchParams.append('userEmail', userEmail);
    }
    if (childId && childId !== '') {
      searchParams = searchParams.append('childIds[]', childId);
    }
    if (clientName && clientName !== '') {
      searchParams = searchParams.append('clientName', clientName);
    }
    if (vehicleStatus && vehicleStatus !== '') {
      searchParams = searchParams.append('vehicleStatus', vehicleStatus);
    }
    
    searchParams = searchParams.append('allChildIds', allChildIds);

    if(licensePlateNumber && licensePlateNumber !== '') {
      searchParams = searchParams.append('licensePlateNumber', licensePlateNumber);
    }
    

    const httpOptions = {
      headers: new HttpHeaders({
        page: page.toString(),
        limit: limit.toString(),
        // withoutCount: withoutCount.toString(),
      }),
      params: searchParams,
    };

    return this.httpClient
      .get(
        apiParameters.url + '/customers/' + customerId + '/subcustomers/vehicles',
        httpOptions
      )
      .pipe(
        map((res:any) => (
          {
            count: res.count || null,
            results: res.results || res
          }
        )),
        map((res: IVehicles) => {
          res.results.sort((a, b) => {
            return (
              (new Date(b.createdAt) as any) - (new Date(a.createdAt) as any)
            );
          });
          return res;
        })
      );
  }

  getByUserId(id: string, page: number, limit: number, withoutCount: boolean): Observable<IVehicles> {
    const httpOptions = {
      headers: new HttpHeaders({
        page: page.toString(),
        limit: limit.toString(),
        // withoutCount: withoutCount.toString(),
      }),
    };
    return this.httpClient
      .get(apiParameters.url + '/users/' + id + '/vehicles', httpOptions)
      .pipe(
        map((res:any) => (
          {
            count: res.count || null,
            results: res.results || res
          }
        )),
        map((res: IVehicles) => {
          // return res;
          res.results.sort((a, b) => {
            return (
              (new Date(b.createdAt) as any) - (new Date(a.createdAt) as any)
            );
          });
          return res;
        })
      );
  }

  /**
   * Gets Vehicle by ID
   * @param id
   * @returns Vehicle
   */
  getById(id): Observable<IVehicleModel> {
    const currentUser = JSON.parse(
      JSON.stringify(this.userService.getLoggedInUser())
    );
    const userId = currentUser.user.id;

    return this.httpClient
      .get(apiParameters.url + '/users/' + userId + '/vehicles/' + id)
      .pipe(
        map((res: IVehicleModel) => {
          return res;
        })
      );
  }

  /**
   * Gets Vehicle by ID
   * @param id
   * @returns Vehicle
   */
  getVehicleByUserId(userId, id): Observable<IVehicleModel> {
    const httpOptions = {
      headers: new HttpHeaders({}),
    };
    return this.httpClient
      .get(
        apiParameters.url + '/users/' + userId + '/vehicles/' + id,
        httpOptions
      )
      .pipe(
        map((res: IVehicleModel) => {
          return res;
        })
      );
  }

  /**
   * Adds vehicle service
   * @param userId - user id
   * @param form
   * @param features
   * @returns Add
   */
  add(userId: string, data, features?: any): Observable<IVehicleInfo> {
    let { vin, licensePlateNumber } = data.form?.form.value || data.vin;

    let body = {
      vin,
    };


    if (Object.keys(body).length === 0 && body.constructor === Object) {
      return;
    }

    if (data.bodyType) {
      body['vehicleBodyTypeId'] = data.bodyType.id;
    }

    if(data.address) {
      body['lat'] = data.address.lat
      body['lng'] = data.address.lng
    }
   
    if (features) {
      body['features'] = features;
    }

    if (data.copyVehicleId) {
      body['copyVehicleId'] =  data.copyVehicleId;
    }

    if (data.forceProfileId) {
      body['forceProfileId'] =  data.forceProfileId;
    }

    if (data.vin) {
      body['vin'] =  data.vin;
    }

    if (licensePlateNumber) {
      body['licensePlateNumber'] = licensePlateNumber;
    }

    const httpOptions = {
      headers: new HttpHeaders({}),
    };

   return this.httpClient
      .post(
        apiParameters.url + '/users/' + userId + '/vehicles',
        body,
        httpOptions
      )
      .pipe(
        map((res: IVehicleModel) => {
          return res;
        })
      );
  }

  patch(vehicleId, body, userId: string) {
    if (userId) {
      return this.httpClient.patch(
        `${apiParameters.url}/users/${userId}/vehicles/${vehicleId}`,
        body
      );
    } else {
      return this.authService.LoggedinUser.pipe(
        take(1),
        concatMap((user) => {
          return this.httpClient.patch(
            `${apiParameters.url}/users/${user.id}/vehicles/${vehicleId}`,
            body
          );
        })
      );
    }
  }

  editVinAsCustomer(vehicle: IVehicleInfo, userId) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    const body = {
      vin: vehicle.vin === '' ? null : vehicle.vin,
    };

    return this.httpClient.patch(
      apiParameters.url + '/users/' + userId + '/vehicles/' + vehicle.id,
      body,
      httpOptions
    );
  }

  // Delete Vehicle
  delete(userId: string, vehicleId: string) {
    const httpOptions = {
      headers: new HttpHeaders({}),
    };
    return this.httpClient.delete(
      apiParameters.url + '/users/' + userId + '/vehicles/' + vehicleId,
      httpOptions
    );
  }

  // Get Images for vehicle
  getImages(vehicleId: string): Observable<IImagesModel[]> {
    const currentUser = JSON.parse(
      JSON.stringify(this.userService.getLoggedInUser())
    );
    const token = currentUser.token;
    const userId = currentUser.user.id;
    const httpOptions = {
      headers: new HttpHeaders({}),
    };
    return this.httpClient
      .get(
        apiParameters.url +
          '/users/' +
          userId +
          '/vehicles/' +
          vehicleId +
          '/files',
        httpOptions
      )
      .pipe(
        map((res: IImagesModel[]) => {
          return res;
        })
      );
  }

  getImagesAsCustomer(
    userId: string,
    vehicleId: string
  ): Observable<IImagesModel[]> {
    const httpOptions = {
      headers: new HttpHeaders({}),
    };
    return this.httpClient
      .get(
        apiParameters.url +
          '/users/' +
          userId +
          '/vehicles/' +
          vehicleId +
          '/files',
        httpOptions
      )
      .pipe(
        map((res: IImagesModel[]) => {
          return res;
        })
      );
  }

  getImagesBlob(fileIds: string[]): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Cache-Control':
          'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
        Pragma: 'no-cache',
        Expires: '0',
      }),
      responseType: 'blob' as 'json',
    };
    const results = [];
    fileIds.forEach((fileId) => {
      results.push(
        this.httpClient.get(
          apiParameters.url + '/files/' + fileId + '/download',
          httpOptions
        )
      );
    });
    return forkJoin(results);
  }

  getImageBlob(fileId: string): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Cache-Control':
          'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
        Pragma: 'no-cache',
        Expires: '0',
      }),
      responseType: 'blob' as 'json',
    };

    return this.httpClient.get(
      apiParameters.url + '/files/' + fileId + '/download',
      httpOptions
    ).pipe(
        map((res: any) => {
          return res;
        })
      );
  }

  downloadFiles(filePath: string[]): Observable<any> {
    const httpOptions = {
      'Content-Type': 'application/json',
    };

    const results = [];
    filePath.forEach((path) => {
      results.push(
        this.httpClient.get(path + '?cacheblock=true', {
          responseType: 'blob',
          headers: httpOptions,
        })
      );
    });
    return forkJoin(results);
  }

  deleteImage(vehicleId: string, fileId: string): Observable<JSON> {
    const currentUser = JSON.parse(
      JSON.stringify(this.userService.getLoggedInUser())
    );
    const token = currentUser.token;
    const userId = currentUser.user.id;
    const httpOptions = {
      headers: new HttpHeaders({}),
    };
    return this.httpClient
      .delete(
        apiParameters.url +
          '/users/' +
          userId +
          '/vehicles/' +
          vehicleId +
          '/files/' +
          fileId,
        httpOptions
      )
      .pipe(
        map((response: JSON) => {
          return response;
        })
      );
  }

  addImage(
    formData: FormData,
    vehicleId: string,
    type?: string
  ): Observable<JSON> {
    let typeText = '';
    if (type && type !== '') {
      typeText = '?type=' + type;
    }

    const currentUser = JSON.parse(
      JSON.stringify(this.userService.getLoggedInUser())
    );
    const userId = currentUser.user.id;
    const httpOptions = {
      reportProgress: true,
      headers: new HttpHeaders({}),
    };
    const url =
      apiParameters.url +
      '/users/' +
      userId +
      '/vehicles/' +
      vehicleId +
      '/files' +
      typeText;
    return this.httpClient.post(url, formData, httpOptions).pipe(
      map((response: JSON) => {
        return response;
      })
    );
  }

  /* ----------- NEW LOGIC FOR IMAGE ------------- */
  getVehicleImages(userId, vehicleId: string): Observable<IImages[]> {
    const httpOptions = {
      headers: new HttpHeaders({}),
    };
    return this.httpClient
      .get(
        apiParameters.url +
          '/users/' +
          userId +
          '/vehicles/' +
          vehicleId +
          '/images',
        httpOptions
      )
      .pipe(
        map((res: IImages[]) => {
          return res;
        })
      );
  }

  deleteNewImage(
    userId: string,
    vehicleId: string,
    imageId: string
  ): Observable<JSON> {
    const httpOptions = {
      headers: new HttpHeaders({}),
    };
    return this.httpClient
      .delete(
        apiParameters.url +
          '/users/' +
          userId +
          '/vehicles/' +
          vehicleId +
          '/images/' +
          imageId,
        httpOptions
      )
      .pipe(
        map((response: JSON) => {
          return response;
        })
      );
  }

  addImageFile(
    formData: FormData,
    imageId: string,
    type: string
  ): Observable<IFile> {
    let typeText = '';
    if (type && type !== '') {
      typeText = '?type=' + type;
    }

    const httpOptions = {
      headers: new HttpHeaders({}),
    };

    return this.httpClient
      .post(
        apiParameters.url + '/images/' + imageId + '/files' + typeText,
        formData,
        httpOptions
      )
      .pipe(
        map((res: IFile) => {
          return res;
        })
      );
  }

  patchImageFile(
    formData: FormData,
    imageId: string,
    fileId: string
  ): Observable<IFile> {
    return this.httpClient
      .patch(
        apiParameters.urlV2 + '/images/' + imageId + '/files/' + fileId,
        formData
      )
      .pipe(
        map((res: IFile) => {
          return res;
        })
      );
  }

  addNewImage(vehicleId, data: any): Observable<IImages> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    const body = data;

    return this.httpClient
      .post(
        apiParameters.url + '/vehicles/' + vehicleId + '/images',
        body,
        httpOptions
      )
      .pipe(
        map((res: IImages) => {
          return res;
        })
      );
  }

  processVehicle(
    userId: string,
    vehicleId: string,
    force: boolean
  ): Observable<any> {
    let params = new HttpParams();

    if (force) {
      params = params.append('force', 'true');
    }

    const httpOptions = {
      params: params,
    };

    return this.httpClient.patch(
      `${apiParameters.urlV2}/users/${userId}/vehicles/${vehicleId}/process`,
      {},
      httpOptions
    );
  }

  importImages(
    userId: string,
    importerId: string,
    vehicleId: string,
    body?: any
  ): Observable<any> {
    return this.httpClient.post(
      `${apiParameters.urlV2}/users/${userId}/importers/${importerId}/import-vehicle-images/${vehicleId}`,
      body
    );
  }

  getTotalImages(userId: string, from: string, to: string): Observable<any> {
    let searchParams = new HttpParams();

    if (from && from !== '') {
      searchParams = searchParams.append('from', from);
    }
    if (to && to !== '') {
      searchParams = searchParams.append('to', to);
    }

    const httpOptions = {
      params: searchParams,
    };

    return this.httpClient.get(
      `${apiParameters.url}/users/${userId}/count-images`,
      httpOptions
    );
  }

  getClientTotalImages(
    clientId: string,
    from: string,
    to: string
  ): Observable<any> {
    let searchParams = new HttpParams();

    if (from && from !== '') {
      searchParams = searchParams.append('from', from);
    }
    if (to && to !== '') {
      searchParams = searchParams.append('to', to);
    }

    const httpOptions = {
      params: searchParams,
    };

    return this.httpClient.get(
      `${apiParameters.url}/clients/${clientId}/count-images`,
      httpOptions
    );
  }

  decodeVin(userId: string, vin: string): Observable<any> {
    return this.httpClient.post(
      `${apiParameters.url}/users/${userId}/vin-decode`,
      { vin }
    );
    // .pipe(
    //   map(res => {
    //     return Object
    //       .entries(res)
    //       .reduce((acc, [key, value]) => {
    //         return { [key.replace(/[-()]/g, '').replace(/\s/g, '_')]: value, ...acc };
    //       }, {});
    //   })
    // );
  }

  deletedVehicleImages(userId: string, vehicleId: string): Observable<any> {
    return this.httpClient.get(
      `${apiParameters.urlV2}/users/${userId}/vehicles/${vehicleId}/images-restore`
    );
  }

  restoreVehicleImage(
    userId: string,
    vehicleId: string,
    imageId: string
  ): Observable<any> {
    return this.httpClient.patch(
      `${apiParameters.urlV2}/users/${userId}/vehicles/${vehicleId}/images/${imageId}/restore`,
      {}
    );
  }

  createBodyType(body): Observable<any> {
    return this.httpClient.post(`${apiParameters.url}/vehicleBodyTypes`, body);
  }

  getBodyTypes(): Observable<any> {
    return this.httpClient.get(`${apiParameters.url}/vehicleBodyTypes`);
  }

  updateBodyType(body, typeId: string): Observable<any> {
    return this.httpClient.patch(
      `${apiParameters.url}/vehicleBodyTypes/${typeId}`,
      body
    );
  }

  deleteBodyType(typeId: string): Observable<any> {
    return this.httpClient.delete(
      `${apiParameters.url}/vehicleBodyTypes/${typeId}`
    );
  }

  countVehicles(userId: string, from?: string, to?: string): Observable<any> {
    let searchParams = new HttpParams();

    if (from && from !== '') {
      searchParams = searchParams.append('from', from);
    }
    if (to && to !== '') {
      searchParams = searchParams.append('to', to);
    }

    const httpOptions = {
      params: searchParams,
    };

    return this.httpClient.get(
      `${apiParameters.url}/users/${userId}/count-vehicles`,
      httpOptions
    );
  }

  uploadOriginalImage(userId: string, vehicleId:string, body:FormData): Observable<any> {
    return this.httpClient.patch(
      `${apiParameters.urlV2}/users/${userId}/vehicles/${vehicleId}/images`, body
    );
  }

  uploadMainImage(imageId:string, files:FormData): Observable<any> {
    return this.httpClient.patch(
      `${apiParameters.urlV2}/images/${imageId}/files/main`, files
    );
  }

  editVehicleFeatures(userId: string, vehicleId:string, features): Observable<any> {
    return this.httpClient.post(
      `${apiParameters.url}/users/${userId}/vehicles/${vehicleId}/features`,features
    )
  }

  processImage(
    userId: string,
    imageId: string,

  ): Observable<any> {
    return this.httpClient.patch(
      `${apiParameters.urlV2}/users/${userId}/images/${imageId}/process`,
      {}
    );
  }

  editVehichle(userId: string, vehicleId:string, body:any) {
      return this.httpClient.patch(
        `${apiParameters.url}/users/${userId}/vehicles/${vehicleId}`,
        body
      );
  }

  getImageMarkers(imageId: string, fileType?: string): Observable<any> {
    let searchParams = new HttpParams();

    searchParams = searchParams.append('fileType', fileType);

    const httpOptions = {
      params: searchParams,
    };

    return this.httpClient.get(
      `${apiParameters.urlV2}/images/${imageId}/markers`,
      httpOptions
    );
  }

  getPlaceholderValues(vehicleId: string, placeholderId: string): Observable<any> {
    return this.httpClient.get(`${apiParameters.urlV2}/vehicles/${vehicleId}/text-placeholders/${placeholderId}/values`);
  }

  setPlaceholderValues(vehicleId: string, body): Observable<any> {
    return this.httpClient.post(`${apiParameters.urlV2}/vehicles/${vehicleId}/text-placeholder-values`, body);
  }

  getVehicleSpecifications(userId: string, vehicleId: string): Observable<any> {
    return this.httpClient.get(`${apiParameters.urlV2}/users/${userId}/vehicles/${vehicleId}/specifications`);
  }

  updateVehicleSpecifications(userId: string, vehicleId: string, body: any) {
    return this.httpClient.patch(`${apiParameters.urlV2}/users/${userId}/vehicles/${vehicleId}/specifications`, body);
  }

  updateVehicleEquipments(userId: string, vehicleId: string, body: any) {
    return this.httpClient.put(`${apiParameters.urlV2}/users/${userId}/vehicles/${vehicleId}/equipments`, body);
  }

  revertVehicles(body): Observable<any> {
    return this.httpClient.post(`${apiParameters.url}/revert-vehicle`, body);
  }

  updateVehicleStatus(userId: string, vehicleId: string, body): Observable<any> {
    return this.httpClient.patch(`${apiParameters.urlV2}/users/${userId}/vehicles/${vehicleId}/status`, body);
  }
}
